import { ProductSalesForecastQuery } from 'services/graphql/main';

export interface ForecastPoint {
  /** Dashed curve: last past week, to join up, then the forecasts. */
  forecastSales: number | null;
  /** The forecast was brought down to the available stock. */
  isCapped: boolean;
  isCurrent: boolean;
  /** The forecast was raised to the level of the sales already observed. */
  isFloored: boolean;

  /** Axis label, « S37 ». */
  label: string;
  /**
   * Balance still to be received at opening, all due dates together: a bar that stays high on
   * the last week says replenishment is far off.
   */
  outstandingSupply: number | null;

  /** Single bar for the weeks ahead. Absent once the projection has stopped. */
  projectedStock: number | null;

  /** Solid curve: past weeks only. */
  realizedSales: number | null;

  /** Sales already made in the current week. Partial, hence absent from the curve. */
  realizedSoFar: number | null;

  /** Stacked bars of the observed weeks, current week included. */
  storeStock: number | null;

  warehouseStock: number | null;

  week: number;

  year: number;
}

type Week = NonNullable<ProductSalesForecastQuery['productSalesForecast']['weeks']>[number];

/** A week is predicted as soon as it carries a forecast. Zero is a forecast, absence is not. */
const isPredicted = (w: Week) => w.predictedNetSalesQuantity !== null && w.predictedNetSalesQuantity !== undefined;

/**
 * Whether anything can be drawn ahead. The current week alone decides: floor, cap and projection
 * all chain from its stock, so without it the weeks after it cannot be drawn either.
 */
export const hasAnyForecast = (data: ProductSalesForecastQuery['productSalesForecast']): boolean => {
  const current = (data.weeks ?? []).find((w) => w.isCurrent);

  return current !== undefined && isPredicted(current);
};

/**
 * Shapes the nine points for the chart. The floor, the cap and the stock projection are applied
 * by the resolver; what is left here is naming, ordering and the join between the two curves.
 */
export const buildForecastPoints = (data: ProductSalesForecastQuery['productSalesForecast']): ForecastPoint[] => {
  const weeks = data.weeks ?? [];
  const currentIndex = weeks.findIndex((w) => w.isCurrent);

  return weeks.map((w, index) => {
    const isFuture = currentIndex >= 0 && index > currentIndex;
    const isLastObserved = currentIndex > 0 && index === currentIndex - 1;

    return {
      label: `S${String(w.week).padStart(2, '0')}`,
      year: w.year,
      week: w.week,
      isCurrent: w.isCurrent,
      storeStock: isFuture ? null : w.storeStock ?? null,
      warehouseStock: isFuture ? null : w.warehouseStock ?? null,
      outstandingSupply: w.outstandingSupply ?? null,
      projectedStock: w.projectedStock ?? null,
      // The current week's sales are partial: putting them on the curve would suggest a
      // collapse. They stay readable in the tooltip.
      realizedSales: w.isCurrent || isFuture ? null : w.netSalesQuantity ?? null,
      // The dashed curve starts on the last solid point so the two meet.
      forecastSales: isLastObserved ? w.netSalesQuantity ?? null : w.forecastSales ?? null,
      realizedSoFar: w.isCurrent ? w.netSalesQuantity ?? null : null,
      isFloored: w.isFloored,
      isCapped: w.isCapped,
    };
  });
};
